import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { StatusBadge } from '@/components/base/StatCard';
import { customers } from '@/mocks/customers';
import { appointments } from '@/mocks/appointments';
import { financeTransactions, currencySymbols, type CurrencyCode } from '@/mocks/cash';
import PaymentModal from './components/PaymentModal';

interface Toast { id: number; message: string; type: 'success' | 'error'; }

const tabs = [
  { id: 'general', label: 'Genel Bilgiler' },
  { id: 'appointments', label: 'Randevu Geçmişi' },
  { id: 'services', label: 'Hizmet Geçmişi' },
  { id: 'packages', label: 'Paket / Seanslar' },
  { id: 'payments', label: 'Ödemeler' },
  { id: 'notes', label: 'Notlar' },
  { id: 'security', label: 'Güvenlik Uyarıları' },
];

export default function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const customer = useMemo(() => customers.find(c => c.id === id), [id]);
  const customerAppointments = useMemo(() =>
    appointments.filter(a => a.customerName === customer?.fullName).sort((a, b) => `${b.date} ${b.time}`.localeCompare(`${a.date} ${a.time}`)),
    [customer]
  );

  // Müşteriye ait tüm ödeme kayıtları (financeTransactions + manuel tahsilatlar)
  const customerPayments = useMemo(() => {
    const fromFinance = financeTransactions.filter(
      t => t.customerName === customer?.fullName && t.type === 'income'
    );
    return fromFinance.sort((a, b) => `${b.date}T${b.time}`.localeCompare(`${a.date}T${a.time}`));
  }, [customer]);

  const showToast = (message: string, type: 'success' | 'error') => {
    const toastId = Date.now();
    setToasts(prev => [...prev, { id: toastId, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== toastId)), 3000);
  };

  const handlePayment = (data: {
    amount: number;
    description: string;
    currency: CurrencyCode;
    method: string;
    bank?: string;
    notes: string;
  }) => {
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toTimeString().slice(0, 5);

    const account = data.method === 'Nakit' ? 'cash' : 'bank';

    const newTx = {
      id: `fin-${Date.now()}`,
      date: dateStr,
      time: timeStr,
      customerName: customer?.fullName,
      description: data.description,
      amount: data.amount,
      currency: data.currency,
      type: 'income' as const,
      account,
      bankName: data.bank,
      paymentType: data.method,
      notes: data.notes,
    };

    // Mevcut mock listeye ekle (mock veri olduğu için)
    financeTransactions.unshift(newTx);

    showToast(
      `${currencySymbols[data.currency]}${data.amount.toLocaleString('tr-TR')} tahsilat kaydedildi.`,
      'success'
    );
    setPaymentModalOpen(false);
    // Tab otomatik ödemelere geçsin
    setActiveTab('payments');
  };

  if (!customer) {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-4">
        <div className="w-16 h-16 flex items-center justify-center rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
          <i className="ri-user-unfollow-line text-2xl" style={{ color: 'var(--text-muted)' }} />
        </div>
        <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Müşteri bulunamadı</p>
        <button onClick={() => navigate('/musteriler')} className="spa-btn-primary text-sm">
          Müşteri Listesine Dön
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map(t => (
          <div key={t.id} className="px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2 animate-slide-in" style={{ background: t.type === 'success' ? '#10B981' : '#EF4444', color: '#fff' }}>
            <div className="w-4 h-4 flex items-center justify-center"><i className={t.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} /></div>
            {t.message}
          </div>
        ))}
      </div>

      {/* Payment Modal */}
      <PaymentModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        onSave={handlePayment}
        customerName={customer.fullName}
      />

      {/* Back + Header */}
      <button
        onClick={() => navigate('/musteriler')}
        className="flex items-center gap-2 text-sm transition-colors cursor-pointer"
        style={{ color: 'var(--text-secondary)' }}
      >
        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line" /></div>
        Müşterilere Dön
      </button>

      {/* Profile Header */}
      <div className="spa-card p-4 md:p-6">
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center text-xl md:text-2xl font-bold text-white flex-shrink-0"
            style={{ background: customer.status === 'Sorunlu' ? '#EF4444' : 'linear-gradient(135deg, var(--gold), var(--gold-dark))' }}>
            {customer.fullName.split(' ').map(n => n[0]).join('')}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg md:text-xl font-bold font-display" style={{ color: 'var(--text-primary)' }}>
                {customer.fullName}
              </h1>
              <StatusBadge status={customer.status}>{customer.status}</StatusBadge>
              {customer.activePackage && (
                <span className="spa-badge" style={{ background: 'rgba(139,92,246,0.12)', color: '#8B5CF6' }}>
                  Paket Aktif
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
              <span className="flex items-center gap-1"><div className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-line text-xs" /></div> {customer.phone}</span>
              <span className="flex items-center gap-1"><div className="w-4 h-4 flex items-center justify-center"><i className="ri-mail-line text-xs" /></div> {customer.email}</span>
              <span className="flex items-center gap-1"><div className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-line text-xs" /></div> {customer.address}</span>
            </div>
          </div>
          <div className="flex gap-2 self-start flex-wrap">
            <button className="spa-btn-secondary text-xs px-3 py-2">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-edit-line text-xs" /></div>
              Düzenle
            </button>
            <button className="spa-btn-primary text-xs px-3 py-2">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-check-line text-xs" /></div>
              Randevu Al
            </button>
            <button
              onClick={() => setPaymentModalOpen(true)}
              className="spa-btn-primary text-xs px-3 py-2 flex items-center gap-2"
              style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-money-dollar-circle-line text-xs" /></div>
              Tahsilat Yap
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-5 border-t" style={{ borderColor: 'var(--border-color)' }}>
          {[
            { label: 'Toplam Ziyaret', value: customer.totalVisits },
            { label: 'Toplam Harcama', value: `₺${customer.totalSpent.toLocaleString('tr-TR')}` },
            { label: 'Açık Ödeme', value: `₺${customer.openPayment.toLocaleString('tr-TR')}` },
            { label: 'Kalan Seans', value: customer.remainingSessions },
          ].map(stat => (
            <div key={stat.label} className="text-center p-2 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
              <p className="text-lg font-bold font-display" style={{ color: 'var(--text-primary)' }}>{stat.value}</p>
              <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2 rounded-spa-sm text-xs md:text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
              activeTab === tab.id ? 'text-white' : ''
            }`}
            style={activeTab === tab.id
              ? { background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))' }
              : { background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="spa-card p-4 md:p-5 min-h-[300px]">
        {activeTab === 'general' && (
          <div className="space-y-4 animate-fade-in">
            <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Genel Bilgiler</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { label: 'Ad Soyad', value: customer.fullName },
                { label: 'Telefon', value: customer.phone },
                { label: 'E-posta', value: customer.email },
                { label: 'Cinsiyet', value: customer.gender },
                { label: 'Doğum Tarihi', value: customer.birthDate ? new Date(customer.birthDate).toLocaleDateString('tr-TR') : '-' },
                { label: 'Geliş Kaynağı', value: customer.source },
                { label: 'Kayıt Tarihi', value: new Date(customer.createdAt).toLocaleDateString('tr-TR') },
                { label: 'Son Geliş', value: customer.lastVisitDate ? new Date(customer.lastVisitDate).toLocaleDateString('tr-TR') : 'Henüz gelmedi' },
                { label: 'Tekrar Müşteri', value: customer.totalVisits > 1 ? 'Evet' : 'Hayır' },
                { label: 'Adres', value: customer.address },
              ].map(field => (
                <div key={field.label} className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                  <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{field.label}</p>
                  <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{field.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'appointments' && (
          <div className="space-y-3 animate-fade-in">
            <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Randevu Geçmişi ({customerAppointments.length})</h3>
            {customerAppointments.length === 0 ? (
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Henüz randevu kaydı yok.</p>
            ) : (
              <div className="space-y-2">
                {customerAppointments.map(appt => (
                  <div key={appt.id} className="flex items-center justify-between p-3 rounded-spa-sm border" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-tertiary)' }}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-spa-sm flex items-center justify-center flex-shrink-0" style={{ background: 'var(--bg-secondary)' }}>
                        <div className="w-5 h-5 flex items-center justify-center"><i className="ri-calendar-check-line text-sm" style={{ color: 'var(--gold)' }} /></div>
                      </div>
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{appt.service}</p>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{appt.date} | {appt.time} | {appt.staff}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={appt.status}>{appt.status}</StatusBadge>
                      <StatusBadge status={appt.paymentStatus}>{appt.paymentStatus}</StatusBadge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'services' && (
          <div className="space-y-3 animate-fade-in">
            <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Hizmet Geçmişi</h3>
            {customerAppointments.length === 0 ? (
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Henüz hizmet kaydı yok.</p>
            ) : (
              <div className="space-y-2">
                {customerAppointments.filter(a => a.status === 'Geldi').map(appt => (
                  <div key={appt.id} className="flex items-center justify-between p-3 rounded-spa-sm border" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-tertiary)' }}>
                    <div>
                      <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{appt.service}</p>
                      <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{appt.date} | {appt.staff}</p>
                    </div>
                    <span className="text-sm font-semibold" style={{ color: 'var(--gold)' }}>₺{appt.price.toLocaleString('tr-TR')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'packages' && (
          <div className="space-y-3 animate-fade-in">
            <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Paket / Seanslar</h3>
            {customer.activePackage ? (
              <div className="p-4 rounded-spa-sm border" style={{ borderColor: 'var(--gold)', background: 'rgba(201,169,98,0.05)' }}>
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>Aktif Paket</p>
                  <span className="spa-badge" style={{ background: 'rgba(139,92,246,0.12)', color: '#8B5CF6' }}>{customer.remainingSessions} seans kaldı</span>
                </div>
                <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'var(--bg-tertiary)' }}>
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${(customer.remainingSessions / 10) * 100}%`,
                      background: customer.remainingSessions <= 2 ? '#EF4444' : 'var(--gold)',
                    }}
                  />
                </div>
                <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                  {customer.remainingSessions <= 2 ? 'Paket bitmek üzere! Yenileme teklif edin.' : 'Paket aktif durumda.'}
                </p>
              </div>
            ) : (
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aktif paket bulunmuyor.</p>
            )}
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="space-y-4 animate-fade-in">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Ödemeler</h3>
              <button
                onClick={() => setPaymentModalOpen(true)}
                className="spa-btn-primary text-xs px-3 py-2 flex items-center gap-2"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-xs" /></div>
                Yeni Tahsilat
              </button>
            </div>

            {/* Özet Kartlar */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-4 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Toplam Harcama</p>
                <p className="text-xl font-bold font-display mt-1" style={{ color: 'var(--text-primary)' }}>₺{customer.totalSpent.toLocaleString('tr-TR')}</p>
              </div>
              <div className="p-4 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Açık Ödeme</p>
                <p className="text-xl font-bold font-display mt-1" style={{ color: customer.openPayment > 0 ? '#EF4444' : '#10B981' }}>
                  ₺{customer.openPayment.toLocaleString('tr-TR')}
                </p>
              </div>
              <div className="p-4 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Tahsilat Sayısı</p>
                <p className="text-xl font-bold font-display mt-1" style={{ color: 'var(--text-primary)' }}>{customerPayments.length}</p>
              </div>
            </div>

            {/* Randevu Ödemeleri */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Randevu Ödemeleri</h4>
              {customerAppointments.length === 0 ? (
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Henüz randevu kaydı yok.</p>
              ) : (
                <div className="space-y-2">
                  {customerAppointments.map(appt => (
                    <div key={appt.id} className="flex items-center justify-between p-3 rounded-spa-sm border" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-tertiary)' }}>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-spa-sm flex items-center justify-center" style={{ background: 'var(--bg-secondary)' }}>
                          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-check-line text-sm" style={{ color: 'var(--gold)' }} /></div>
                        </div>
                        <div>
                          <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{appt.service}</p>
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{appt.date}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>₺{appt.price.toLocaleString('tr-TR')}</span>
                        <StatusBadge status={appt.paymentStatus}>{appt.paymentStatus}</StatusBadge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Tahsilat Geçmişi */}
            {customerPayments.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Tahsilat Geçmişi</h4>
                <div className="space-y-2">
                  {customerPayments.map(pay => (
                    <div key={pay.id} className="flex items-center justify-between p-3 rounded-spa-sm border" style={{ borderColor: 'var(--border-color)', background: 'rgba(16,185,129,0.04)' }}>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-spa-sm flex items-center justify-center" style={{ background: 'rgba(16,185,129,0.1)' }}>
                          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-money-dollar-circle-line text-sm" style={{ color: '#10B981' }} /></div>
                        </div>
                        <div>
                          <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{pay.description}</p>
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{pay.date} {pay.time} · {pay.paymentType}{pay.bankName ? ` · ${pay.bankName}` : ''}</p>
                        </div>
                      </div>
                      <span className="text-sm font-bold font-display" style={{ color: '#10B981' }}>
                        +{currencySymbols[pay.currency || 'TRY']}{pay.amount.toLocaleString('tr-TR')}
                        {pay.currency !== 'TRY' && <span className="text-[10px] font-normal ml-1" style={{ color: 'var(--text-muted)' }}>{pay.currency}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="space-y-4 animate-fade-in">
            <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Notlar</h3>
            {customer.satisfactionNote && (
              <div className="p-4 rounded-spa-sm border" style={{ borderColor: 'rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.05)' }}>
                <p className="text-xs font-medium mb-1" style={{ color: '#10B981' }}><div className="w-4 h-4 flex items-center justify-center inline-flex"><i className="ri-emotion-happy-line text-xs" /></div> Memnuniyet Notu</p>
                <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{customer.satisfactionNote}</p>
              </div>
            )}
            {customer.privateNotes && (
              <div className="p-4 rounded-spa-sm border" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-tertiary)' }}>
                <p className="text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}><div className="w-4 h-4 flex items-center justify-center inline-flex"><i className="ri-lock-line text-xs" /></div> Özel Notlar</p>
                <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{customer.privateNotes}</p>
              </div>
            )}
            {!customer.satisfactionNote && !customer.privateNotes && (
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Henüz not eklenmemiş.</p>
            )}
          </div>
        )}

        {activeTab === 'security' && (
          <div className="space-y-4 animate-fade-in">
            <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Güvenlik Uyarıları</h3>
            {customer.status === 'Sorunlu' || customer.status === 'Yasaklı' ? (
              <div className="p-4 rounded-spa-sm border" style={{ borderColor: '#EF4444', background: 'rgba(239,68,68,0.05)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: 'rgba(239,68,68,0.15)' }}>
                    <i className="ri-error-warning-line" style={{ color: '#EF4444' }} />
                  </div>
                  <p className="font-semibold" style={{ color: '#EF4444' }}>Sorunlu Müşteri Uyarısı</p>
                </div>
                <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                  Bu müşteri son 3 randevusuna gelmemiştir. Kaporasız randevu vermeyiniz. WhatsApp teyitli randevulara da gelmemiştir.
                </p>
              </div>
            ) : (
              <div className="p-4 rounded-spa-sm border" style={{ borderColor: 'rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.05)' }}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: 'rgba(16,185,129,0.15)' }}>
                    <i className="ri-shield-check-line" style={{ color: '#10B981' }} />
                  </div>
                  <p className="text-sm font-medium" style={{ color: '#10B981' }}>Güvenlik uyarısı bulunmuyor.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}